import 'react-native-gesture-handler';
import 'react-native-reanimated';
import { Redirect, Stack, useSegments, usePathname} from 'expo-router';
import { useEffect, useState } from "react";
import { OverlayProvider, Chat } from "stream-chat-expo";
import { getStreamClient } from "@/services/stream/client";
import { getRecordingPermissionsAsync, requestRecordingPermissionsAsync } from 'expo-audio';
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { getPassportToken, getStreamAuth, getUserProfile, logout } from "@/services/auth/tokenStore";
import {NotificationProvider} from "@/services/notifications/provider";
import { authEventEmitter } from "@/services/auth/authEvents";
import { setupCallKeep } from "@/services/calls/callKeep";
import * as Notifications from "expo-notifications";
import { useUpdates } from "@/hooks/useUpdates";
import { UpdateNotificationBar } from "@/components/UpdateNotificationBar";
import { ChatImage } from "@/components/chat/ChatImage";
import { initSentry, Sentry } from "@/services/sentry";

initSentry();

export const unstable_settings = {
    anchor: '(tabs)',
};
Notifications.setNotificationHandler({
    handleNotification: async (notification) => {
        // Stream sends data in the trigger payload
        const data = (notification.request.trigger as any)?.payload;

        // Suppress Stream notifications if user is in the channel
        if (data && data.stream && data.stream.channel_id) {
            // Check if user is currently in this channel
            const currentChannelId = (global as any).__CURRENT_CHANNEL_ID__;
            const notificationChannelId = data.stream.channel_id;

            if (currentChannelId && currentChannelId === notificationChannelId) {
                return {
                    shouldPlaySound: false,
                    shouldSetBadge: false,
                    shouldShowBanner: false,
                    shouldShowList: false,
                };
            }
        }

        return {
            shouldPlaySound: true,
            shouldSetBadge: true,
            shouldShowBanner: true,
            shouldShowList: true,
        };
    },
});

function RootLayout() {
    const { isUpdateAvailable, performUpdate } = useUpdates();
    const segments = useSegments();

    const [ready, setReady] = useState(false);
    const [authenticated, setAuthenticated] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function init() {
            try {
                // Ask for the microphone up front (voice notes, calls). The audio
                // session itself is deliberately NOT configured or activated here:
                // voice notes set their own mode right before recording
                // (CustomAudioRecordingButton), and during a call CallKit owns the
                // iOS session — an app-wide playAndRecord/doNotMix session activated
                // at startup was one of the things fighting it (see useRingback.ts).
                try {
                    const { status } = await getRecordingPermissionsAsync();
                    if (status !== 'granted') {
                        await requestRecordingPermissionsAsync();
                    }
                } catch (audioErr) {
                    console.log("Audio permission error:", audioErr);
                }

                const passportToken = await getPassportToken();
                const streamAuth = await getStreamAuth();

                if (passportToken && streamAuth) {
                    //console.log('PassportToken:', passportToken)
                    const client = getStreamClient();
                    if (!client.userID) {
                        const { name, image } = await getUserProfile();
                        await client.connectUser(
                            { 
                                id: streamAuth.userId,
                                name: name ?? undefined,
                                image: image ?? undefined,
                            },
                            streamAuth.token
                        );
                    }
                    if (!cancelled) {
                        setAuthenticated(true);
                    }
                } else {
                    if (!cancelled) {
                        setAuthenticated(false);
                    }
                }

                if (!cancelled) setReady(true);
            } catch (err) {
                console.log("Initialization error:", err);
                if (!cancelled) setReady(true);
            }
        }

        init();
        
        const unsubscribe = authEventEmitter.subscribe(() => {
            logout();
        });

        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, []);

    // Register with CallKit / ConnectionService once so outbound calls get
    // native call treatment (see services/calls/callKeep.ts).
    useEffect(() => {
        setupCallKeep();
    }, []);

    if (!ready) return null;

    const inAuthGroup = segments[0] === '(auth)';
    if (authenticated && inAuthGroup) {
        return <Redirect href="/(tabs)" />;
    }
    if (!authenticated && !inAuthGroup) {
        return <Redirect href="/(auth)/login" />;
    }

    return (
        <NotificationProvider>
            {isUpdateAvailable && (
                <UpdateNotificationBar onUpdate={performUpdate} />
            )}
            <GestureHandlerRootView style={{ flex: 1 }}>
                <SafeAreaProvider>
                    <OverlayProvider>
                        <Chat client={getStreamClient()} ImageComponent={ChatImage}>
                            <Stack screenOptions={{ headerShown: false }}>
                                <Stack.Screen name="(tabs)" />
                                <Stack.Screen
                                    name="chat/[channelId]"
                                    options={{
                                        headerShown: false,
                                    }}
                                />
                                <Stack.Screen
                                    name="call"
                                    options={{
                                        headerShown: false,
                                        presentation: 'fullScreenModal',
                                        gestureEnabled: false,
                                    }}
                                />
                            </Stack>
                        </Chat>
                    </OverlayProvider>
                </SafeAreaProvider>
            </GestureHandlerRootView>
        </NotificationProvider>
    );
}

export default Sentry.wrap(RootLayout);
